import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Chip, StatCard, LoadingState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Plus, PencilSimple, Trash, Phone, EnvelopeSimple, MapPin, Buildings, IdentificationBadge,
    MagnifyingGlass, Wrench, ToggleLeft, ToggleRight, Calendar, ClockClockwise, X, WarningCircle,
    User, Storefront, ArrowSquareOut,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link, useLocation, useNavigate } from "react-router-dom";

const SERVICE_CATEGORIES = [
    "plumbing", "electrical", "civil_structural", "lift_elevator", "water_supply",
    "drainage_sewage", "housekeeping", "security", "common_area", "parking",
    "gardening", "pest_control", "internet_dtv", "generator_power_backup",
    "fire_safety", "amenities", "staff_service", "other",
];

const catLabel = (c) => (c || "").replace(/_/g, " ").replace(/\b\w/g, s => s.toUpperCase());
const statusLabel = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, ch => ch.toUpperCase());

const emptyForm = () => ({
    entity_type: "STAFF",
    name: "",
    role_label: "",
    services: [],
    phone: "",
    alternate_phone: "",
    email: "",
    address: "",
    vendor_org: "",
    contact_person: "",
    gst_number: "",
    vendor_reference: "",
    employee_reference: "",
    joining_date: "",
    contract_start: "",
    contract_end: "",
    notes: "",
    is_active: true,
});

function fmt(dt) {
    if (!dt) return "—";
    try { return new Date(dt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return dt; }
}

// ---------- Detail panel ----------
function ProviderDetail({ id, onClose, onChanged }) {
    const [doc, setDoc] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const [d, h] = await Promise.all([
                api.get(`/staff/${id}`),
                api.get(`/staff/${id}/history?limit=50`),
            ]);
            setDoc(d.data);
            setHistory(h.data);
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    async function toggleActive() {
        try {
            await api.post(`/staff/${id}/${doc.is_active ? "deactivate" : "activate"}`);
            toast.success(doc.is_active ? "Deactivated" : "Activated");
            await load();
            onChanged?.();
        } catch (e) { toast.error(formatError(e)); }
    }

    if (loading || !doc) return <LoadingState message="Loading provider..." />;
    const counts = doc.assignment_counts || {};
    const openStates = ["new", "acknowledged", "assigned", "in_progress", "on_hold", "reopened"];
    const targetLabel = (c) => c.target_type === "COMMON_AREA" ? `${c.common_area || "Common"}${c.common_area_location ? " · " + c.common_area_location : ""}` : `${c.target_flat_label || "—"}${c.target_resident_name ? " · " + c.target_resident_name : ""}`;

    return (
        <div className="space-y-6" data-testid="provider-detail-dialog">
            {/* Header */}
            <div className="border-b border-brand-line pb-4">
                <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                        <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft flex items-center gap-1">
                            {doc.entity_type === "VENDOR" ? <Storefront size={11} weight="duotone" /> : <User size={11} weight="duotone" />}
                            {doc.entity_type} · {doc.role_label}
                        </p>
                        <h2 className="font-heading text-xl md:text-2xl tracking-tight text-brand-ink mt-1" data-testid="provider-detail-name">{doc.name}</h2>
                        {doc.vendor_org && <p className="text-sm text-brand-inkSoft">{doc.vendor_org}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Chip variant={doc.is_active ? "success" : "neutral"} data-testid="provider-detail-status">
                            {doc.is_active ? "Active" : "Inactive"}
                        </Chip>
                        <Button size="sm" onClick={toggleActive}
                            data-testid={`provider-toggle-${doc.is_active ? "deactivate" : "activate"}`}
                            className={`rounded-full ${doc.is_active ? "bg-brand-action hover:bg-brand-actionHover text-white" : "bg-brand-ink hover:bg-brand-ink/90 text-white"}`}>
                            {doc.is_active ? <><ToggleLeft size={14} className="mr-1" /> Deactivate</> : <><ToggleRight size={14} className="mr-1" /> Activate</>}
                        </Button>
                    </div>
                </div>
                {/* Services */}
                <div className="flex flex-wrap gap-1.5 mt-3" data-testid="provider-detail-services">
                    {doc.services.map(s => <Chip key={s} variant="default">{catLabel(s)}</Chip>)}
                    {doc.services.length === 0 && <span className="text-xs text-brand-inkSoft italic">No service categories</span>}
                </div>
            </div>

            {/* Info grid */}
            <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                    {doc.contact_person && <div className="flex justify-between"><span className="text-brand-inkSoft">Contact person</span><span className="font-medium text-brand-ink">{doc.contact_person}</span></div>}
                    {doc.phone && <div className="flex justify-between"><span className="text-brand-inkSoft">Mobile</span><a href={`tel:${doc.phone}`} className="font-medium text-brand-action hover:underline">{doc.phone}</a></div>}
                    {doc.alternate_phone && <div className="flex justify-between"><span className="text-brand-inkSoft">Alt Mobile</span><a href={`tel:${doc.alternate_phone}`} className="font-medium text-brand-action hover:underline">{doc.alternate_phone}</a></div>}
                    {doc.email && <div className="flex justify-between"><span className="text-brand-inkSoft">Email</span><a href={`mailto:${doc.email}`} className="font-medium text-brand-action hover:underline">{doc.email}</a></div>}
                    {doc.address && <div className="flex justify-between"><span className="text-brand-inkSoft">Address</span><span className="font-medium text-brand-ink text-right max-w-[60%]">{doc.address}</span></div>}
                </div>
                <div className="space-y-2">
                    {doc.entity_type === "STAFF" && doc.employee_reference && <div className="flex justify-between"><span className="text-brand-inkSoft">Employee ref</span><span className="font-medium text-brand-ink">{doc.employee_reference}</span></div>}
                    {doc.entity_type === "STAFF" && doc.joining_date && <div className="flex justify-between"><span className="text-brand-inkSoft">Joined</span><span className="font-medium text-brand-ink">{fmt(doc.joining_date)}</span></div>}
                    {doc.entity_type === "VENDOR" && doc.vendor_reference && <div className="flex justify-between"><span className="text-brand-inkSoft">Vendor ref</span><span className="font-medium text-brand-ink">{doc.vendor_reference}</span></div>}
                    {doc.entity_type === "VENDOR" && doc.gst_number && <div className="flex justify-between"><span className="text-brand-inkSoft">GST</span><span className="font-medium text-brand-ink font-mono">{doc.gst_number}</span></div>}
                    {doc.entity_type === "VENDOR" && (doc.contract_start || doc.contract_end) && (
                        <div className="flex justify-between"><span className="text-brand-inkSoft">Contract</span><span className="font-medium text-brand-ink">{fmt(doc.contract_start)} → {fmt(doc.contract_end)}</span></div>
                    )}
                    {doc.notes && <div className="pt-2 border-t border-brand-line"><p className="text-xs text-brand-inkSoft italic">"{doc.notes}"</p></div>}
                </div>
            </div>

            {/* Operational metrics */}
            <div>
                <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-3">Operational Summary</p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="provider-detail-counts">
                    <StatCard label="Open" value={counts.open_all || 0} accent testId="provider-count-open" />
                    <StatCard label="In Progress" value={counts.in_progress || 0} testId="provider-count-in-progress" />
                    <StatCard label="Resolved" value={counts.resolved || 0} testId="provider-count-resolved" />
                    <StatCard label="Closed" value={counts.closed || 0} testId="provider-count-closed" />
                    <StatCard label="Avg Resolution" value={`${doc.avg_resolution_hours || 0}h`} hint="Last 100" testId="provider-count-avg" />
                </div>
            </div>

            {/* Service history */}
            <div>
                <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-3 flex items-center gap-1">
                    <ClockClockwise size={12} weight="duotone" /> Service History ({history.length})
                </p>
                {history.length === 0 ? (
                    <p className="text-xs text-brand-inkSoft italic">No assignments yet.</p>
                ) : (
                    <div className="overflow-x-auto border border-brand-line rounded-sm">
                        <table className="w-full text-sm min-w-[700px]" data-testid="provider-service-history">
                            <thead>
                                <tr className="bg-brand-bg border-b border-brand-line text-left">
                                    <th className="py-2 px-3 text-[10px] uppercase tracking-overline text-brand-inkSoft">Complaint #</th>
                                    <th className="py-2 px-3 text-[10px] uppercase tracking-overline text-brand-inkSoft">Target</th>
                                    <th className="py-2 px-3 text-[10px] uppercase tracking-overline text-brand-inkSoft">Category</th>
                                    <th className="py-2 px-3 text-[10px] uppercase tracking-overline text-brand-inkSoft">Priority</th>
                                    <th className="py-2 px-3 text-[10px] uppercase tracking-overline text-brand-inkSoft">Status</th>
                                    <th className="py-2 px-3 text-[10px] uppercase tracking-overline text-brand-inkSoft">Assigned</th>
                                    <th className="py-2 px-3 text-[10px] uppercase tracking-overline text-brand-inkSoft">Resolved</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map(c => (
                                    <tr key={c.id} className="border-b border-brand-line/60 hover:bg-brand-bg/50" data-testid={`provider-history-${c.id}`}>
                                        <td className="py-2 px-3 font-mono text-xs text-brand-ink">
                                            <Link to="/app/complaints" className="hover:underline text-brand-ink inline-flex items-center gap-1">{c.complaint_number || "—"}<ArrowSquareOut size={10} /></Link>
                                        </td>
                                        <td className="py-2 px-3 text-brand-ink text-xs">{targetLabel(c)}</td>
                                        <td className="py-2 px-3 text-brand-inkSoft text-xs">{catLabel(c.category)}</td>
                                        <td className="py-2 px-3"><Chip variant={c.priority === "urgent" || c.priority === "high" ? "danger" : "default"}>{statusLabel(c.priority)}</Chip></td>
                                        <td className="py-2 px-3"><Chip variant={openStates.includes(c.status) ? "warn" : c.status === "closed" ? "neutral" : "success"}>{statusLabel(c.status)}</Chip></td>
                                        <td className="py-2 px-3 text-brand-inkSoft text-xs">{fmt(c.assigned_at)}</td>
                                        <td className="py-2 px-3 text-brand-inkSoft text-xs">{fmt(c.resolved_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

// ---------- Main page ----------
export default function Staff() {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const isStaff = user?.role === "admin" || user?.role === "committee";
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState(null);

    const [q, setQ] = useState("");
    const [entityFilter, setEntityFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("active");
    const [serviceFilter, setServiceFilter] = useState("all");
    const [sortBy, setSortBy] = useState("name");

    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [selectedId, setSelectedId] = useState(null);

    async function load() {
        setLoading(true);
        try {
            const params = {};
            if (entityFilter !== "all") params.entity_type = entityFilter;
            if (statusFilter === "active") params.active = "true";
            else if (statusFilter === "inactive") params.active = "false";
            if (serviceFilter !== "all") params.service = serviceFilter;
            if (q.trim()) params.q = q.trim();
            if (sortBy) params.sort = sortBy;
            const [list, sm] = await Promise.all([
                api.get("/staff", { params }),
                api.get("/staff/summary"),
            ]);
            setRows(list.data);
            setSummary(sm.data);
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityFilter, statusFilter, serviceFilter, sortBy]);

    function openCreate(entityType = "STAFF") {
        setEditing(null);
        setForm({ ...emptyForm(), entity_type: entityType });
        setFormOpen(true);
    }

    // Dashboard Quick Action deep-link: navigate here with state={{ autoOpenCreate: true }}
    useEffect(() => {
        if (isStaff && location.state?.autoOpenCreate) {
            openCreate(location.state?.entityType || "STAFF");
            navigate(location.pathname, { replace: true, state: {} });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStaff]);

    function openEdit(row) {
        setEditing(row);
        setForm({
            entity_type: row.entity_type || (row.vendor_org ? "VENDOR" : "STAFF"),
            name: row.name || "",
            role_label: row.role_label || "",
            services: row.services || (row.category ? [row.category] : []),
            phone: row.phone || "",
            alternate_phone: row.alternate_phone || "",
            email: row.email || "",
            address: row.address || "",
            vendor_org: row.vendor_org || "",
            contact_person: row.contact_person || "",
            gst_number: row.gst_number || "",
            vendor_reference: row.vendor_reference || "",
            employee_reference: row.employee_reference || "",
            joining_date: row.joining_date || "",
            contract_start: row.contract_start || "",
            contract_end: row.contract_end || "",
            notes: row.notes || "",
            is_active: row.is_active !== false,
        });
        setFormOpen(true);
    }

    async function submitForm(e) {
        e.preventDefault();
        if (!form.name.trim() || !form.role_label.trim()) { toast.error("Name and role are required"); return; }
        if (!form.services.length) { toast.error("Select at least one service category"); return; }
        if (form.entity_type === "VENDOR" && !form.vendor_org.trim()) { toast.error("Vendor / company name is required"); return; }
        const payload = { ...form };
        // Clean empty strings for optional dates
        for (const k of ["joining_date", "contract_start", "contract_end", "email", "gst_number", "employee_reference", "vendor_reference", "contact_person", "alternate_phone", "address", "notes"]) {
            if (!payload[k]) delete payload[k];
        }
        if (payload.entity_type === "STAFF") delete payload.vendor_org;
        try {
            if (editing) {
                await api.patch(`/staff/${editing.id}`, payload);
                toast.success("Provider updated");
            } else {
                const { data } = await api.post("/staff", payload);
                if (data?.duplicate_of) toast.warning("A provider with same mobile already exists — saved anyway.");
                else toast.success("Provider added");
            }
            setFormOpen(false); load();
        } catch (e) { toast.error(formatError(e)); }
    }

    async function toggleActive(row) {
        try {
            await api.post(`/staff/${row.id}/${row.is_active ? "deactivate" : "activate"}`);
            toast.success(row.is_active ? "Deactivated" : "Activated");
            load();
        } catch (e) { toast.error(formatError(e)); }
    }

    async function removeRow(row) {
        if (!window.confirm(`Remove ${row.name}? If they have complaint history the record will be archived (soft-deleted) to preserve history.`)) return;
        try {
            const { data } = await api.delete(`/staff/${row.id}`);
            toast.success(data.archived ? "Archived (has history)" : "Deleted");
            load();
        } catch (e) { toast.error(formatError(e)); }
    }

    function toggleService(s) {
        setForm(prev => {
            const has = prev.services.includes(s);
            return { ...prev, services: has ? prev.services.filter(x => x !== s) : [...prev.services, s] };
        });
    }

    const filteredCount = rows.length;

    if (!isStaff) {
        return <EmptyState title="Not available" description="Only admin/committee can access Staff & Vendors." />;
    }

    return (
        <div className="animate-fade-up space-y-6" data-testid="staff-page">
            <PageHeader
                overline="Operations"
                title="Staff & Vendors"
                description="Manage the people and vendors who deliver services to your society."
                actions={
                    <div className="flex gap-2">
                        <Button data-testid="staff-add-btn" onClick={() => openCreate("STAFF")} className="rounded-full bg-brand-ink hover:bg-brand-ink/90 text-white">
                            <Plus size={16} className="mr-1.5" /> Add Staff
                        </Button>
                        <Button data-testid="vendor-add-btn" onClick={() => openCreate("VENDOR")} className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white">
                            <Plus size={16} className="mr-1.5" /> Add Vendor
                        </Button>
                    </div>
                }
            />

            {/* Summary */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="staff-summary">
                    <StatCard label="Total Providers" value={summary.total_providers} testId="staff-summary-total" />
                    <StatCard label="Active Staff" value={summary.active_staff} testId="staff-summary-active-staff" />
                    <StatCard label="Active Vendors" value={summary.active_vendors} testId="staff-summary-active-vendors" />
                    <StatCard label="Inactive" value={summary.inactive} testId="staff-summary-inactive" />
                    <StatCard label="Currently Assigned" value={summary.currently_assigned} accent testId="staff-summary-assigned" />
                    <StatCard label="Open Assignments" value={summary.open_assignments} accent testId="staff-summary-open" />
                </div>
            )}

            {/* Filters */}
            <div className="bg-white border border-brand-line rounded-sm p-4" data-testid="staff-filters">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <div className="md:col-span-2 relative">
                        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-inkSoft" />
                        <Input data-testid="staff-search" placeholder="Search name, contact, phone, service..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="rounded-sm border-brand-line pl-9" />
                    </div>
                    <Select value={entityFilter} onValueChange={setEntityFilter}>
                        <SelectTrigger data-testid="staff-entity-filter" className="rounded-sm border-brand-line"><SelectValue placeholder="Type" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All types</SelectItem>
                            <SelectItem value="STAFF">Staff</SelectItem>
                            <SelectItem value="VENDOR">Vendor</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger data-testid="staff-status-filter" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            <SelectItem value="active">Active only</SelectItem>
                            <SelectItem value="inactive">Inactive only</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={serviceFilter} onValueChange={setServiceFilter}>
                        <SelectTrigger data-testid="staff-service-filter" className="rounded-sm border-brand-line"><SelectValue placeholder="Service" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All services</SelectItem>
                            {SERVICE_CATEGORIES.map(s => <SelectItem key={s} value={s}>{catLabel(s)}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                    <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger data-testid="staff-sort" className="rounded-sm border-brand-line w-52"><SelectValue placeholder="Sort" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="name">Name (A→Z)</SelectItem>
                            <SelectItem value="recent">Recently added</SelectItem>
                            <SelectItem value="assignments">Active assignments</SelectItem>
                        </SelectContent>
                    </Select>
                    <div className="flex gap-2 items-center">
                        <span className="text-xs text-brand-inkSoft">{filteredCount} shown</span>
                        <Button variant="ghost" size="sm" onClick={load} className="text-brand-ink"><MagnifyingGlass size={14} className="mr-1" /> Search</Button>
                        <Button variant="ghost" size="sm" onClick={() => { setQ(""); setEntityFilter("all"); setStatusFilter("active"); setServiceFilter("all"); setSortBy("name"); }} className="text-brand-inkSoft">Clear</Button>
                    </div>
                </div>
            </div>

            {/* List */}
            {loading ? <LoadingState message="Loading providers..." /> :
                rows.length === 0 ? <EmptyState title="No providers match" description="Try adjusting filters or add a new provider." /> : (
                    <div className="bg-white border border-brand-line rounded-sm overflow-hidden" data-testid="staff-list">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm min-w-[900px]">
                                <thead>
                                    <tr className="bg-brand-bg border-b border-brand-line text-left">
                                        <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">Name</th>
                                        <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">Type</th>
                                        <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">Services</th>
                                        <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">Contact</th>
                                        <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">Status</th>
                                        <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">Active</th>
                                        <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(r => (
                                        <tr key={r.id} data-testid={`staff-row-${r.id}`} className="border-b border-brand-line/60 hover:bg-brand-bg/50">
                                            <td className="py-3 px-4">
                                                <button onClick={() => setSelectedId(r.id)} className="text-brand-ink font-medium hover:underline text-left" data-testid={`staff-open-detail-${r.id}`}>
                                                    {r.name}
                                                </button>
                                                {r.vendor_org && <p className="text-[10px] text-brand-inkSoft mt-0.5">{r.vendor_org}</p>}
                                                {r.role_label && <p className="text-[10px] text-brand-inkSoft mt-0.5">{r.role_label}</p>}
                                            </td>
                                            <td className="py-3 px-4">
                                                <Chip variant={r.entity_type === "VENDOR" ? "warn" : "default"}>
                                                    {r.entity_type === "VENDOR" ? <><Storefront size={11} weight="duotone" className="inline mr-1" />Vendor</> : <><User size={11} weight="duotone" className="inline mr-1" />Staff</>}
                                                </Chip>
                                            </td>
                                            <td className="py-3 px-4">
                                                <div className="flex flex-wrap gap-1 max-w-xs">
                                                    {r.services.slice(0, 3).map(s => <Chip key={s} variant="neutral">{catLabel(s)}</Chip>)}
                                                    {r.services.length > 3 && <span className="text-[10px] text-brand-inkSoft">+{r.services.length - 3}</span>}
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-xs text-brand-inkSoft">
                                                {r.phone && <div className="flex items-center gap-1"><Phone size={11} weight="duotone" />{r.phone}</div>}
                                                {r.email && <div className="flex items-center gap-1 mt-1"><EnvelopeSimple size={11} weight="duotone" />{r.email}</div>}
                                            </td>
                                            <td className="py-3 px-4">
                                                <Chip variant={r.is_active ? "success" : "neutral"}>{r.is_active ? "Active" : "Inactive"}</Chip>
                                            </td>
                                            <td className="py-3 px-4 text-brand-inkSoft"><Wrench size={11} weight="duotone" className="inline mr-1" />{r.open_assignments} open</td>
                                            <td className="py-3 px-4 text-right whitespace-nowrap">
                                                <Button size="sm" variant="ghost" onClick={() => toggleActive(r)} data-testid={`staff-toggle-${r.id}`} className="text-brand-inkSoft hover:text-brand-ink">
                                                    {r.is_active ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                                                </Button>
                                                <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`staff-edit-${r.id}`} className="text-brand-inkSoft hover:text-brand-ink">
                                                    <PencilSimple size={14} />
                                                </Button>
                                                <Button size="sm" variant="ghost" onClick={() => removeRow(r)} data-testid={`staff-delete-${r.id}`} className="text-brand-action">
                                                    <Trash size={14} />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

            {/* Add/Edit dialog */}
            <Dialog open={formOpen} onOpenChange={setFormOpen}>
                <DialogContent className="rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl tracking-tight">{editing ? "Edit Provider" : (form.entity_type === "VENDOR" ? "Add Vendor" : "Add Staff")}</DialogTitle>
                        <DialogDescription>{form.entity_type === "VENDOR" ? "External service company or agency." : "Person directly working for the society."}</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submitForm} className="space-y-4">
                        {/* Entity type toggle (only for new) */}
                        {!editing && (
                            <div className="flex gap-2">
                                <Button type="button" onClick={() => setForm({ ...form, entity_type: "STAFF" })} data-testid="staff-form-type-staff"
                                    className={`flex-1 rounded-full ${form.entity_type === "STAFF" ? "bg-brand-ink text-white" : "bg-white border border-brand-line text-brand-ink"}`}>
                                    <User size={14} className="mr-1" /> Staff
                                </Button>
                                <Button type="button" onClick={() => setForm({ ...form, entity_type: "VENDOR" })} data-testid="staff-form-type-vendor"
                                    className={`flex-1 rounded-full ${form.entity_type === "VENDOR" ? "bg-brand-ink text-white" : "bg-white border border-brand-line text-brand-ink"}`}>
                                    <Storefront size={14} className="mr-1" /> Vendor
                                </Button>
                            </div>
                        )}

                        <div className="grid md:grid-cols-2 gap-3">
                            <div>
                                <Label>{form.entity_type === "VENDOR" ? "Vendor / Company *" : "Full name *"}</Label>
                                <Input data-testid="staff-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm border-brand-line" />
                            </div>
                            <div>
                                <Label>Role / Service label *</Label>
                                <Input data-testid="staff-form-role" required value={form.role_label} onChange={(e) => setForm({ ...form, role_label: e.target.value })} className="rounded-sm border-brand-line" placeholder="e.g. Plumber, Lift Vendor" />
                            </div>
                        </div>

                        {form.entity_type === "VENDOR" && (
                            <div className="grid md:grid-cols-2 gap-3">
                                <div>
                                    <Label>Vendor org (company name)</Label>
                                    <Input data-testid="staff-form-vendor-org" value={form.vendor_org} onChange={(e) => setForm({ ...form, vendor_org: e.target.value })} className="rounded-sm border-brand-line" placeholder="e.g. ABC Lift Services Pvt Ltd" />
                                </div>
                                <div>
                                    <Label>Contact person</Label>
                                    <Input data-testid="staff-form-contact-person" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="rounded-sm border-brand-line" />
                                </div>
                            </div>
                        )}

                        <div>
                            <Label>Service categories *</Label>
                            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="staff-form-services">
                                {SERVICE_CATEGORIES.map(s => (
                                    <button type="button" key={s} onClick={() => toggleService(s)} data-testid={`staff-form-service-${s}`}
                                        className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${form.services.includes(s) ? "bg-brand-ink text-white border-brand-ink" : "bg-white text-brand-inkSoft border-brand-line hover:border-brand-ink"}`}>
                                        {catLabel(s)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-3">
                            <div>
                                <Label>Mobile</Label>
                                <Input data-testid="staff-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm border-brand-line" placeholder="+91" />
                            </div>
                            <div>
                                <Label>Alternate mobile</Label>
                                <Input data-testid="staff-form-alt-phone" value={form.alternate_phone} onChange={(e) => setForm({ ...form, alternate_phone: e.target.value })} className="rounded-sm border-brand-line" />
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input data-testid="staff-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm border-brand-line" />
                            </div>
                            <div>
                                <Label>Address</Label>
                                <Input data-testid="staff-form-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-sm border-brand-line" />
                            </div>
                        </div>

                        {form.entity_type === "STAFF" && (
                            <div className="grid md:grid-cols-2 gap-3">
                                <div>
                                    <Label>Employee reference</Label>
                                    <Input data-testid="staff-form-emp-ref" value={form.employee_reference} onChange={(e) => setForm({ ...form, employee_reference: e.target.value })} className="rounded-sm border-brand-line" />
                                </div>
                                <div>
                                    <Label>Joining date</Label>
                                    <Input data-testid="staff-form-joining" type="date" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} className="rounded-sm border-brand-line" />
                                </div>
                            </div>
                        )}

                        {form.entity_type === "VENDOR" && (
                            <div className="grid md:grid-cols-2 gap-3">
                                <div>
                                    <Label>Vendor reference</Label>
                                    <Input data-testid="staff-form-vendor-ref" value={form.vendor_reference} onChange={(e) => setForm({ ...form, vendor_reference: e.target.value })} className="rounded-sm border-brand-line" />
                                </div>
                                <div>
                                    <Label>GST number</Label>
                                    <Input data-testid="staff-form-gst" value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value })} className="rounded-sm border-brand-line font-mono" placeholder="e.g. 27ABCDE1234F1Z5" />
                                </div>
                                <div>
                                    <Label>Contract start</Label>
                                    <Input data-testid="staff-form-contract-start" type="date" value={form.contract_start} onChange={(e) => setForm({ ...form, contract_start: e.target.value })} className="rounded-sm border-brand-line" />
                                </div>
                                <div>
                                    <Label>Contract end</Label>
                                    <Input data-testid="staff-form-contract-end" type="date" value={form.contract_end} onChange={(e) => setForm({ ...form, contract_end: e.target.value })} className="rounded-sm border-brand-line" />
                                </div>
                            </div>
                        )}

                        <div>
                            <Label>Notes</Label>
                            <Textarea data-testid="staff-form-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm border-brand-line" />
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setFormOpen(false)} className="rounded-full">Cancel</Button>
                            <Button type="submit" data-testid="staff-form-submit" className="rounded-full bg-brand-action hover:bg-brand-actionHover">{editing ? "Save changes" : "Create"}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Detail dialog */}
            <Dialog open={!!selectedId} onOpenChange={(v) => !v && setSelectedId(null)}>
                <DialogContent className="rounded-sm max-w-4xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader className="sr-only">
                        <DialogTitle>Provider details</DialogTitle>
                        <DialogDescription>Provider profile, operational metrics and service history.</DialogDescription>
                    </DialogHeader>
                    <button onClick={() => setSelectedId(null)} className="absolute right-4 top-4 text-brand-inkSoft hover:text-brand-ink" aria-label="Close"><X size={18} /></button>
                    {selectedId && <ProviderDetail id={selectedId} onClose={() => setSelectedId(null)} onChanged={load} />}
                </DialogContent>
            </Dialog>
        </div>
    );
}
